import { useEffect, useState } from "react";
import api, { formatApiError, formatMoney, todayLocalIso } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BarChart3, TrendingUp, Wallet, FileSpreadsheet, FileText, Printer } from "lucide-react";
import { exportToExcel, exportToPdf } from "@/lib/exportUtils";

function KPI({ label, value, color, subtitle }) {
  return (
    <Card className="p-5 border-zinc-200">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
      <div className={`font-manrope font-black text-3xl mt-1 tabular-nums ${color || "text-zinc-900"}`}>{value}</div>
      {subtitle && <div className="text-xs text-zinc-500 mt-1">{subtitle}</div>}
    </Card>
  );
}

export default function ReporteVisual() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const today = todayLocalIso();
  const firstDay = today.slice(0, 8) + "01";
  const [range, setRange] = useState({ desde: firstDay, hasta: today });
  const [conciliacion, setConciliacion] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/reporte-visual?desde=${range.desde}&hasta=${range.hasta}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveConciliacion = async (tipo, entidad_id, sistema) => {
    const fisico = Number(conciliacion[entidad_id]);
    if (isNaN(fisico)) { toast.error("Ingresá el saldo físico"); return; }
    try {
      await api.post("/conciliacion-fisica", { tipo, entidad_id, saldo_sistema: sistema, saldo_fisico: fisico });
      toast.success(`Conciliación guardada. Diferencia: ${formatMoney(fisico - sistema)}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data && !loading) return <div className="text-zinc-500">Cargando…</div>;

  return (
    <div className="space-y-6" data-testid="reporte-visual-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Reportes</div>
        <h1 className="font-manrope font-black text-4xl mt-1 flex items-center gap-3"><BarChart3 className="w-8 h-8" /> Reporte Visual</h1>
      </header>

      <Card className="p-4 border-zinc-200">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label>Desde</Label><Input type="date" value={range.desde} onChange={(e) => setRange({ ...range, desde: e.target.value })} className="w-40" /></div>
          <div><Label>Hasta</Label><Input type="date" value={range.hasta} onChange={(e) => setRange({ ...range, hasta: e.target.value })} className="w-40" /></div>
          <Button onClick={load} className="bg-[#002FA7] hover:bg-[#00227A]">Actualizar</Button>
          {data && (
            <>
              <Button variant="outline" onClick={() => {
                const rows = [
                  { concepto: "1. Ventas totales", monto: data.ventas_totales.total },
                  { concepto: "2. Caja ingresada manualmente", monto: data.caja_ingresada.total },
                  { concepto: "3. CxC del mes anterior facturada este mes", monto: data.cxc_facturada.total },
                  { concepto: "4. Facturado del mes actual", monto: data.facturado_5.facturado_actual },
                  { concepto: "5. Pendiente de facturar (informativo)", monto: data.pendiente_facturar_cxc.total },
                  { concepto: "6. Subtotal (4 + 3 + 2)", monto: data.facturado_5.total },
                  { concepto: "7. Total gastos", monto: data.gastos.total },
                  { concepto: "8. Ganancia (6 − 7)", monto: data.resultado.resultado },
                ];
                const columns = [{ key: "concepto", label: "Concepto", width: 44 }, { key: "monto", label: "Monto", type: "money", width: 20 }];
                exportToExcel({ title: "Reporte Visual", filters: { Desde: range.desde, Hasta: range.hasta }, columns, rows, filename: `reporte-visual-${range.desde}` });
              }} data-testid="rv-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
              </Button>
              <Button variant="outline" onClick={() => {
                const rows = [
                  { concepto: "1. Ventas totales", monto: data.ventas_totales.total },
                  { concepto: "2. Caja ingresada manualmente", monto: data.caja_ingresada.total },
                  { concepto: "3. CxC del mes anterior facturada este mes", monto: data.cxc_facturada.total },
                  { concepto: "4. Facturado del mes actual", monto: data.facturado_5.facturado_actual },
                  { concepto: "5. Pendiente de facturar (informativo)", monto: data.pendiente_facturar_cxc.total },
                  { concepto: "6. Subtotal (4 + 3 + 2)", monto: data.facturado_5.total },
                  { concepto: "7. Total gastos", monto: data.gastos.total },
                  { concepto: "8. Ganancia (6 − 7)", monto: data.resultado.resultado },
                ];
                const columns = [{ key: "concepto", label: "Concepto", width: 44 }, { key: "monto", label: "Monto", type: "money", width: 20 }];
                exportToPdf({ title: "Reporte Visual", filters: { Desde: range.desde, Hasta: range.hasta }, columns, rows, filename: `reporte-visual-${range.desde}` });
              }} data-testid="rv-pdf">
                <FileText className="w-4 h-4 mr-2" /> PDF
              </Button>
              <Button variant="outline" onClick={() => window.print()} data-testid="rv-print">
                <Printer className="w-4 h-4 mr-2" /> Imprimir
              </Button>
            </>
          )}
        </div>
      </Card>

      {data && (
        <>
          {/* 1. Ventas totales — Ronda 20 (con desglose CxC / post-cierre) */}
          <div>
            <h2 className="font-semibold text-lg mb-2">1️⃣ Ventas Totales</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <KPI label="Total ventas" value={formatMoney(data.ventas_totales.total)} color="text-blue-700" subtitle={`${data.ventas_totales.count} pedidos`} />
              {data.ventas_split && (
                <>
                  <KPI label="Ventas CxC (facturadas)" value={formatMoney(data.ventas_split.ventas_cxc || 0)} color="text-amber-700" subtitle={`${data.ventas_split.ventas_cxc_count || 0} pedidos anteriores al cierre`} />
                  <KPI label="Ventas después del cierre" value={formatMoney(data.ventas_split.ventas_despues_cierre || 0)} color="text-emerald-700" subtitle={`${data.ventas_split.ventas_despues_cierre_count || 0} pedidos del período`} />
                </>
              )}
            </div>
          </div>

          {/* 2. Caja ingresada */}
          <div>
            <h2 className="font-semibold text-lg mb-2">2️⃣ Caja ingresada manualmente</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <KPI label="Total ingresado" value={formatMoney(data.caja_ingresada.total)} color="text-emerald-700" subtitle="Sin descontar egresos · Todas las cajas" />
            </div>
            {Array.isArray(data.caja_ingresada.por_caja) && data.caja_ingresada.por_caja.length > 0 && (
              <div className="mt-3 border border-zinc-200 rounded-md overflow-hidden" data-testid="caja-ingresada-por-caja">
                <div className="px-3 py-2 bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Desglose por caja</div>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-zinc-100">
                    {data.caja_ingresada.por_caja.map((c) => (
                      <tr key={c.caja_id}>
                        <td className="px-3 py-2 font-medium">{c.nombre}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{formatMoney(c.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* 3. CxC facturada */}
          <div>
            <h2 className="font-semibold text-lg mb-2">3️⃣ CxC del mes anterior ya facturada</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <KPI label="CxC facturada en periodo" value={formatMoney(data.cxc_facturada.total)} color="text-blue-700" subtitle={`${data.cxc_facturada.count} pedidos`} />
              <a href={`/cxc-facturada?desde=${range.desde}&hasta=${range.hasta}`}><Button variant="outline" className="w-full h-full">Ver pedidos CxC facturados →</Button></a>
            </div>
          </div>

          {/* 4. Pendiente facturar CxC */}
          <div>
            <h2 className="font-semibold text-lg mb-2">4️⃣ Pendiente de facturar de CxC</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <KPI label="Pendiente facturar" value={formatMoney(data.pendiente_facturar_cxc.total)} color="text-orange-700" subtitle={`${data.pendiente_facturar_cxc.count} pedidos`} />
            </div>
          </div>

          {/* 5. Facturado combinado */}
          <div>
            <h2 className="font-semibold text-lg mb-2">5️⃣ Facturado + CxC + Caja ingresada</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <KPI label="Facturado del mes" value={formatMoney(data.facturado_5.facturado_actual)} />
              <KPI label="CxC facturada" value={formatMoney(data.facturado_5.cxc_facturada)} />
              <KPI label="Caja ingresada" value={formatMoney(data.facturado_5.caja_ingresada)} />
              <KPI label="TOTAL 5" value={formatMoney(data.facturado_5.total)} color="text-blue-700" />
            </div>
          </div>

          {/* 6. Total gastos */}
          <div>
            <h2 className="font-semibold text-lg mb-2">6️⃣ Total de gastos</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <KPI label="Gastos del periodo" value={formatMoney(data.gastos.total)} color="text-red-600" subtitle={`${data.gastos.count} registros`} />
            </div>
          </div>

          {/* 7. Resultado */}
          <div>
            <h2 className="font-semibold text-lg mb-2">7️⃣ Resultado (5 - 6)</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <KPI label="Ingresos" value={formatMoney(data.resultado.ingresos)} />
              <KPI label="Gastos" value={formatMoney(data.resultado.gastos)} color="text-red-600" />
              <KPI label="Resultado" value={formatMoney(data.resultado.resultado)} color={data.resultado.resultado >= 0 ? "text-emerald-700" : "text-red-600"} />
            </div>
          </div>

          {/* 8. Saldos + Conciliación física */}
          <div>
            <h2 className="font-semibold text-lg mb-2">8️⃣ Disponible + Conciliación física</h2>

            <div className="mb-3">
              <div className="text-sm font-semibold text-zinc-700 mb-2 flex items-center gap-2"><Wallet className="w-4 h-4" />Cajas</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.saldos.cajas.map(c => {
                  const fisico = Number(conciliacion[c.id] || 0);
                  const diff = fisico - c.saldo_sistema;
                  return (
                    <Card key={c.id} className="p-4 border-zinc-200">
                      <div className="font-semibold">{c.nombre}</div>
                      <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                        <div><div className="text-[10px] text-zinc-500 uppercase">Sistema</div><div className="font-semibold">{formatMoney(c.saldo_sistema)}</div></div>
                        <div><div className="text-[10px] text-zinc-500 uppercase">Físico</div><Input type="number" step="0.01" value={conciliacion[c.id] || ""} onChange={(e) => setConciliacion({ ...conciliacion, [c.id]: e.target.value })} className="h-8" /></div>
                        <div><div className="text-[10px] text-zinc-500 uppercase">Diferencia</div><div className={`font-semibold ${diff === 0 ? "" : diff > 0 ? "text-emerald-700" : "text-red-600"}`}>{formatMoney(diff)}</div></div>
                      </div>
                      {conciliacion[c.id] && <Button size="sm" className="mt-2 bg-[#002FA7] hover:bg-[#00227A]" onClick={() => saveConciliacion("caja", c.id, c.saldo_sistema)}>Guardar</Button>}
                    </Card>
                  );
                })}
              </div>
            </div>

            <div className="mb-3">
              <div className="text-sm font-semibold text-zinc-700 mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4" />Saldos empresas de transporte</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {data.saldos.transportistas.map(t => (
                  <Card key={t.id} className="p-4 border-zinc-200">
                    <div className="font-semibold">{t.nombre}</div>
                    <div className={`font-manrope font-black text-2xl mt-1 tabular-nums ${t.saldo < 100 ? "text-red-600" : "text-zinc-900"}`}>{formatMoney(t.saldo)}</div>
                  </Card>
                ))}
                {data.saldos.transportistas.length === 0 && <div className="text-zinc-400 text-sm col-span-full">Sin empresas prepagadas activas.</div>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
